<template>
  <div class="speaker-customization-panel">
    <div class="panel-header">
      <div class="panel-title-area">
        <h3 class="panel-title">Speaker Customization</h3>
        <span v-if="totalCustomizations > 0" class="customization-count">({{ totalCustomizations }})</span>
      </div>
      <button class="toggle-btn" @click="togglePanel">
        {{ isPanelExpanded ? '▼' : '▶' }}
      </button>
    </div>

    <div v-show="isPanelExpanded" class="panel-content">
      <!-- Background customization section -->
      <div class="settings-section">
        <h4 class="settings-title">Transcript Appearance</h4>

        <!-- Background opacity slider -->
        <div class="transcript-customization">
          <div class="customization-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="9" cy="9" r="2"></circle>
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
            </svg>
            <span>Background Opacity</span>
          </div>
          <div class="slider-container">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              v-model="transcriptOpacity"
              class="opacity-slider"
              @change="handleTranscriptOpacityChange"
            />
            <span class="opacity-value">{{ Math.round(transcriptOpacity * 100) }}%</span>
          </div>
        </div>

        <!-- Background color picker -->
        <div class="transcript-customization">
          <div class="customization-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
              <circle cx="12" cy="12" r="2"></circle>
            </svg>
            <span>Background Color</span>
          </div>
          <div class="color-picker-container">
            <div class="current-color" :style="{ backgroundColor: transcriptBackgroundColor }"></div>
            <input
              type="color"
              v-model="transcriptBackgroundColor"
              class="color-picker"
              @change="handleTranscriptColorChange"
            />
            <button class="reset-button" @click="resetTranscriptBackground">Reset to Default</button>
          </div>
        </div>

        <h4 class="settings-title">Podcast Background</h4>
        <div class="background-upload">
          <label :for="`background-upload-${messageId}`" class="upload-bg-label">
            <div class="bg-preview-container">
              <img
                v-if="backgroundImage"
                :src="backgroundImage"
                class="bg-preview"
                alt="Background Preview"
              />
              <div v-else class="bg-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                  <circle cx="9" cy="9" r="2"></circle>
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                </svg>
                <span>Upload Background</span>
              </div>
            </div>
          </label>
          <input
            :id="`background-upload-${messageId}`"
            type="file"
            accept="image/*"
            class="file-input"
            @change="handleBackgroundUpload"
          />
          <button
            v-if="backgroundImage"
            class="reset-background-button"
            @click="resetBackgroundImage"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18"></path>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
            </svg>
            Reset Background
          </button>
        </div>
      </div>

      <!-- Speaker customization -->
      <div class="settings-section">
        <h4 class="settings-title">Customize Speakers</h4>
        <div class="speakers-grid">
          <div
            v-for="(speaker, index) in internalSpeakers"
            :key="`customize-${index}`"
            class="speaker-customize-item"
          >
            <div class="speaker-image-upload">
              <label :for="`image-upload-${messageId}_${index}`" class="upload-label">
                <div class="preview-container">
                  <img
                    v-if="uploadedImages[`${messageId}_${index}`]"
                    :src="uploadedImages[`${messageId}_${index}`]"
                    class="image-preview"
                    alt="Preview"
                  />
                  <div v-else class="upload-placeholder">
                    <span>{{ speaker.name }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 5v14"></path>
                      <path d="M5 12h14"></path>
                    </svg>
                  </div>
                </div>
              </label>
              <input
                :id="`image-upload-${messageId}_${index}`"
                type="file"
                accept="image/*"
                class="file-input"
                @change="(e) => handleImageUpload(e, index)"
              />
            </div>
            <button
              v-if="uploadedImages[`${messageId}_${index}`]"
              class="reset-image-button"
              @click="resetSpeakerImage(index)"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
              Reset Image
            </button>

            <div class="speaker-fields">
              <input
                type="text"
                v-model="internalSpeakers[index].name"
                class="name-input"
                placeholder="Name"
                @change="handleNameChange(index)"
              />

              <input
                type="text"
                v-model="internalSpeakers[index].role"
                class="role-input"
                placeholder="Role"
                @change="handleRoleChange(index)"
              />

             <div class="voice-select-container" :class="{ 'voice-selected': selectedVoices[index] }" :data-selected-voice="getReadableVoiceName(selectedVoices[index])">
                <div class="voice-select-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                    <line x1="12" y1="19" x2="12" y2="23"></line>
                    <line x1="8" y1="23" x2="16" y2="23"></line>
                  </svg>
                  <span>Voice Selection</span>
                </div>

                <select
                  v-model="selectedVoices[index]"
                  class="voice-select"
                  @change="handleVoiceChange(index)"
                >
                  <option value="">Default Voice</option>
                  <optgroup label="Female">
                    <option v-for="voice in femaleVoices" :key="`female-${voice}`" :value="voice">
                      {{ getReadableVoiceName(voice) }}
                    </option>
                  </optgroup>
                  <optgroup label="Male">
                    <option v-for="voice in maleVoices" :key="`male-${voice}`" :value="voice">
                      {{ getReadableVoiceName(voice) }}
                    </option>
                  </optgroup>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import API from '@/api';
import { mapActions } from 'vuex';

export default {
  name: 'SpeakerCustomizationPanel',
  props: {
    isPro: {
      type: Boolean,
      default: false
    },
    messageId: {
      required: true
    },
    speakers: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      isPanelExpanded: false,
      transcriptOpacity: 0.6,
      transcriptBackgroundColor: '#000000',
      // Create a deep copy of speakers to safely modify
      internalSpeakers: JSON.parse(JSON.stringify(this.speakers)),
      // Store uploaded images
      uploadedImages: {},
      // Background image
      backgroundImage: null,
      // Store selected voices
      selectedVoices: {},
      // Available voices by gender
      femaleVoices: [
        'af_alloy', 'af_aoede', 'af_bella', 'af_heart', 'af_jadzia', 'af_jessica',
        'af_kore', 'af_nicole', 'af_nova', 'af_river', 'af_sarah', 'af_sky',
        'af_v0', 'af_v0bella', 'af_v0irulan', 'af_v0nicole', 'af_v0sarah', 'af_v0sky',
        'bf_alice', 'bf_emma', 'bf_lily', 'bf_v0emma', 'bf_v0isabella',
        'ef_dora', 'ff_siwis', 'hf_alpha', 'hf_beta', 'if_sara', 'jf_alpha',
        'jf_gongitsune', 'jf_nezumi', 'jf_tebukuro', 'pf_dora', 'zf_xiaobei',
        'zf_xiaoni', 'zf_xiaoxiao', 'zf_xiaoyi'
      ],
      maleVoices: [
        'am_adam', 'am_echo', 'am_eric', 'am_fenrir', 'am_liam', 'am_michael',
        'am_onyx', 'am_puck', 'am_santa', 'am_v0adam', 'am_v0gurney', 'am_v0michael',
        'bm_daniel', 'bm_fable', 'bm_george', 'bm_lewis', 'bm_v0george', 'bm_v0lewis',
        'em_alex', 'em_santa', 'hm_omega', 'hm_psi', 'im_nicola', 'jm_kumo',
        'pm_alex', 'pm_santa', 'zm_yunjian', 'zm_yunxi', 'zm_yunxia', 'zm_yunyang'
      ]
    }
  },
  computed: {
    totalCustomizations() {
      let count = 0;

      // Count transcript customizations
      if (this.transcriptOpacity !== 0.6 || this.transcriptBackgroundColor !== '#000000') {
        count++;
      }

      // Count background image
      if (this.backgroundImage) {
        count++;
      }

      // Count speaker customizations
      this.internalSpeakers.forEach((speaker, index) => {
        const key = `${this.messageId}_${index}`;
        if (this.uploadedImages[key]) count++;
        if (this.selectedVoices[index]) count++;
      });

      return count;
    }
  },
  watch: {
    // Watch for changes to props speakers array
    speakers: {
      handler(newSpeakers) {
        // Deep copy to avoid reference issues
        this.internalSpeakers = JSON.parse(JSON.stringify(newSpeakers));
      },
      deep: true,
      immediate: true
    }
  },
  async mounted() {
    try {
      this.initializeDefaultVoices();
      // First, apply any custom data that may have been passed via props
      // (This would be data that the parent component already loaded)
      this.applyExistingCustomizations();

      // Then load customization data from backend
      await this.loadCustomizationData();

      // Emit that customizations are loaded and ready
      this.$emit('customizations-loaded', {
        speakers: this.internalSpeakers.map((speaker, index) => ({
          original_name: this.speakers[index]?.name || speaker.name,
          custom_name: speaker.name,
          original_role: this.speakers[index]?.role || speaker.role,
          custom_role: speaker.role,
          voice_id: this.selectedVoices[index] || null
        }))
      });
    } catch (error) {
      console.error('Error loading customization data:', error);
    }
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),
    async resetBackgroundImage() {
      if (!this.requiresPro('Resetting background image')) {
        return;
      }

      try {
        // Call API to delete the background image
        await API.delete(`/user_customization/messages/${this.messageId}/background`);

        // Reset background image in component state
        this.backgroundImage = null;

        // Emit event to notify parent component
        this.$emit('background-reset', {
          messageId: this.messageId
        });

        // Update UI
        this.updateRelatedComponents();

        // Also update customizations in the data store
        await this.saveSpeakerCustomizations();

        // Show temporary success message
        this.showTempMessageFn('Background image has been reset', 'success');
      } catch (error) {
        console.error('Error resetting background image:', error);
        this.showTempMessageFn('Failed to reset background image', 'error');
      }
    },
    async resetSpeakerImage(index) {
      if (!this.requiresPro('Resetting speaker images')) {
        return;
      }

      try {
        // Create a unique key using messageId and index
        const uniqueKey = `${this.messageId}_${index}`;

        // Call API to delete the image
        await API.delete(`/user_customization/messages/${this.messageId}/speakers/${index}/image`);

        // Remove the image from the component state
        this.$delete(this.uploadedImages, uniqueKey);

        // Emit event to notify parent component
        this.$emit('image-reset', {
          messageId: this.messageId,
          index,
          uniqueKey,
          speakerName: this.internalSpeakers[index].name,
        });

        // Update UI
        this.updateRelatedComponents();

        // Show temporary success message
        this.showTempMessageFn('Speaker image has been reset', 'success');
      } catch (error) {
        console.error('Error resetting speaker image:', error);
        this.showTempMessageFn('Failed to reset speaker image', 'error');
      }
    },

    togglePanel() {
      // Allow all users to open the panel
      this.isPanelExpanded = !this.isPanelExpanded;
    },

    // Pro check wrapper method
    requiresPro(action, callback) {
      if (callback) callback();
      return true;
    },

    // Use the same methods from the original SpeakerPanel
    handleTranscriptOpacityChange() {
      this.requiresPro('Adjusting transcript opacity', () => {
        this.updateTranscriptBackground();
      });
    },

    handleTranscriptColorChange() {
      this.requiresPro('Changing transcript color', () => {
        this.updateTranscriptBackground();
      });
    },

    resetTranscriptBackground() {
      this.requiresPro('Resetting transcript background', () => {
        this.transcriptOpacity = 0.6;
        this.transcriptBackgroundColor = '#000000';
        this.updateTranscriptBackground();
      });
    },

    updateTranscriptBackground(fromServer = false) {
      // Check if it's already in rgba format (when loaded from server)
      if (this.transcriptBackgroundColor.startsWith('rgba(') && fromServer) {
        // Already in rgba format, use directly
        this.$emit('transcript-background-changed', this.transcriptBackgroundColor);
        this.saveTranscriptCustomization(this.transcriptBackgroundColor);
        return;
      }

      // Convert hex color to rgb
      const hex = this.transcriptBackgroundColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Create rgba string
      const backgroundColor = `rgba(${r}, ${g}, ${b}, ${this.transcriptOpacity})`;

      // Emit event to update transcript background
      this.$emit('transcript-background-changed', backgroundColor);

      // Update local storage
      localStorage.setItem(`transcript_background_${this.messageId}`, backgroundColor);

      // Save customization
      this.saveTranscriptCustomization(backgroundColor);
    },

    async saveTranscriptCustomization(backgroundColor) {
      // only try to save if the user is logged in
      if (!localStorage.getItem('token')) return;
      try {
        await API.post(
          `/user_customization/messages/${this.messageId}/transcript-customization`,
          {
            background_color: backgroundColor,
            opacity: this.transcriptOpacity,
            color: this.transcriptBackgroundColor
          }
        );
      } catch (error) {
        console.error('Error saving transcript customization:', error);
      }
    },

    initializeDefaultVoices() {
      // Automatically assign default voices based on role or name hints
      this.internalSpeakers.forEach((speaker, index) => {
        // If we already have a voice assigned, don't override it
        if (this.selectedVoices[index]) return;

        const name = speaker.name.toLowerCase();
        const role = (speaker.role || '').toLowerCase();

        // Determine if likely male or female based on role or name
        let isLikelyMale = false;

        // Check common male indicators in role
        if (role.includes('host') || role.includes('moderator')) {
          isLikelyMale = Math.random() > 0.5; // 50/50 chance for neutral roles
        } else if (role.includes('mr') || role.includes('male') || role.includes('man') ||
                  role.includes('father') || role.includes('brother') || role.includes('uncle')) {
          isLikelyMale = true;
        }

        // Choose default voice from appropriate list
        if (isLikelyMale) {
          // Assign a standard male voice
          this.$set(this.selectedVoices, index, this.maleVoices[0]);
        } else {
          // Assign a standard female voice
          this.$set(this.selectedVoices, index, this.femaleVoices[0]);
        }
      });

      // Force update the component to reflect changes
      this.$forceUpdate();
    },

    async loadCustomizationData() {
      try {
        const token = localStorage.getItem('token');
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");

        // Get background and image customizations
        const url = token ? `/user_customization/messages/${this.messageId}/customize-data` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/customize-data`;
        const response = await API.get(url);
        const data = response.data;

        // Load transcript customization
        if (data.transcriptCustomization) {
          const bgColor = data.transcriptCustomization.background_color;
          const color = data.transcriptCustomization.color;
          const opacity = data.transcriptCustomization.opacity;
          if (bgColor && opacity) {
            this.transcriptBackgroundColor = color;
            this.transcriptOpacity = data.transcriptCustomization.opacity;
            localStorage.setItem(`transcript_background_${this.messageId}`, color);
            this.updateTranscriptBackground(true);
          }
        }

        // Load speaker images
        if (data.speakerImages) {
          Object.keys(data.speakerImages).forEach(index => {
            const img = data.speakerImages[index];
            const uniqueKey = `${this.messageId}_${index}`;
            this.$set(this.uploadedImages, uniqueKey, img.url);
          });
        }

        // Load background image
        if (data.background) {
          this.backgroundImage = data.background.url;
          this.$emit('background-uploaded', {
            imageUrl: data.background.url,
            id: data.background.id
          });
        }

        // Get speaker customizations
        const speakers_url = token ? `/user_customization/messages/${this.messageId}/speaker-customizations` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/speaker-customizations`;
        const speakerCustomResponse = await API.get(speakers_url);
        const customData = speakerCustomResponse.data;

        if (customData && customData.speakers && customData.speakers.length) {
          // Apply customizations to internal speakers using simple index-based approach
          customData.speakers.forEach(customSpeaker => {
            // If speaker_index is provided, use it directly
            if (customSpeaker.speaker_index !== undefined &&
                customSpeaker.speaker_index < this.internalSpeakers.length) {

              const index = customSpeaker.speaker_index;

              // Apply custom name
              if (customSpeaker.custom_name) {
                this.internalSpeakers[index].name = customSpeaker.custom_name;
              }

              // Apply custom role
              if (customSpeaker.custom_role) {
                this.internalSpeakers[index].role = customSpeaker.custom_role;
              }

              // Apply voice selection
              if (customSpeaker.voice_id) {
                this.$set(this.selectedVoices, index, customSpeaker.voice_id);
              }
            }
            // Fall back to matching by name if no index or index is invalid
            else {
              // Find speaker by name
              const speakerIndex = this.internalSpeakers.findIndex(s =>
                s.name === customSpeaker.original_name
              );

              if (speakerIndex >= 0) {
                // Apply custom name
                if (customSpeaker.custom_name) {
                  this.internalSpeakers[speakerIndex].name = customSpeaker.custom_name;
                }

                // Apply custom role
                if (customSpeaker.custom_role) {
                  this.internalSpeakers[speakerIndex].role = customSpeaker.custom_role;
                }

                // Apply voice selection
                if (customSpeaker.voice_id) {
                  this.$set(this.selectedVoices, speakerIndex, customSpeaker.voice_id);
                }
              }
            }
          });

          // Force UI update
          this.$forceUpdate();

          // Emit event that customizations were loaded
          this.$emit('customizations-loaded', customData);
        }

        return customData;
      } catch (error) {
        console.error('Error loading customization data:', error);
        return null;
      }
    },

    updateRelatedComponents() {
      // Emit event to notify parent that UI needs updating
      this.$emit('ui-update-needed');

      // Force update on this component
      this.$forceUpdate();
    },

    applyExistingCustomizations() {
      // Check if parent component passed any pre-loaded data via props
      if (this.speakers && this.speakers.length) {
        // Update internal speakers based on the props
        this.internalSpeakers = JSON.parse(JSON.stringify(this.speakers));
      }
    },

    async saveSpeakerCustomizations() {
      try {
        // Format the speaker customizations data
        const speakerData = this.internalSpeakers.map((speaker, index) => ({
          original_name: this.speakers[index].name,
          custom_name: speaker.name,
          original_role: this.speakers[index].role,
          custom_role: speaker.role,
          voice_id: this.selectedVoices[index] || null,
          speaker_index: index
        }));

        // Create the customizations payload
        const customizations = {
          speakers: speakerData,
          background_settings: {
            use_custom_background: !!this.backgroundImage
          }
        };

        // Send to backend
        await API.post(
          `/user_customization/messages/${this.messageId}/speaker-customizations`,
          customizations
        );

      } catch (error) {
        console.error('Error saving speaker customizations:', error);
      }
    },

    // Handle background image upload
    async handleBackgroundUpload(event) {
      if (!this.requiresPro('Uploading custom backgrounds')) {
        // Reset the file input
        event.target.value = '';
        return;
      }

      const file = event.target.files[0];
      if (!file) return;

      // Check if file is an image
      if (file.type.startsWith('image/') || file.name.endsWith('.webp')) {
        try {
          // Create a FormData object
          const formData = new FormData();
          formData.append('file', file);

          // Upload to backend
          const response = await API.post(`/user_customization/messages/${this.messageId}/background`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          // Update local state with the returned URL
          this.backgroundImage = response.data.url;

          // Emit event that background was uploaded
          this.$emit('background-uploaded', {
            imageUrl: response.data.url,
            id: response.data.id
          });
        } catch (error) {
          console.error('Error uploading background image:', error);
        }
      }
    },

    // Get a readable name from voice ID
    getReadableVoiceName(voiceId) {
      if (!voiceId) return 'Default Voice';

      // Parse the voice ID into readable parts
      const parts = voiceId.split('_');
      if (parts.length < 2) return voiceId;

      let gender = '';
      let region = '';
      let name = parts[1];

      // Determine gender and region
      switch(parts[0]) {
        case 'af': gender = 'Female'; region = 'American'; break;
        case 'am': gender = 'Male'; region = 'American'; break;
        case 'bf': gender = 'Female'; region = 'British'; break;
        case 'bm': gender = 'Male'; region = 'British'; break;
        case 'ef': gender = 'Female'; region = 'Spanish'; break;
        case 'em': gender = 'Male'; region = 'Spanish'; break;
        case 'hf': gender = 'Female'; region = 'Hindi'; break;
        case 'hm': gender = 'Male'; region = 'Hindi'; break;
        case 'if': gender = 'Female'; region = 'Italian'; break;
        case 'im': gender = 'Male'; region = 'Italian'; break;
        case 'jf': gender = 'Female'; region = 'Japanese'; break;
        case 'jm': gender = 'Male'; region = 'Japanese'; break;
        case 'pf': gender = 'Female'; region = 'Portuguese'; break;
        case 'pm': gender = 'Male'; region = 'Portuguese'; break;
        case 'zf': gender = 'Female'; region = 'Chinese'; break;
        case 'zm': gender = 'Male'; region = 'Chinese'; break;
        default: return voiceId;
      }

      // Capitalize name
      name = name.charAt(0).toUpperCase() + name.slice(1);

      return `${name} (${region})`;
    },

    // Handle image upload
    async handleImageUpload(event, index) {
      if (!this.requiresPro('Uploading speaker images')) {
        // Reset the file input
        event.target.value = '';
        return;
      }

      const file = event.target.files[0];
      if (!file) return;

      // Check if file is an image
      if (file.type.startsWith('image/') || file.name.endsWith('.webp')) {
        try {
          // Create a FormData object
          const formData = new FormData();
          formData.append('file', file);
          formData.append('original_name', this.internalSpeakers[index].name);

          // Upload to backend
          const response = await API.post(
            `/user_customization/messages/${this.messageId}/speakers/${index}/image`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          // Create a unique key using messageId and index
          const uniqueKey = `${this.messageId}_${index}`;

          // Update the image in the component state
          this.$set(this.uploadedImages, uniqueKey, response.data.url);

          // Emit event with all necessary info
          this.$emit('image-uploaded', {
            messageId: this.messageId,
            index,
            speakerName: this.internalSpeakers[index].name,
            imageUrl: response.data.url,
            id: response.data.id
          });
        } catch (error) {
          console.error('Error uploading speaker image:', error);
        }
      }
    },

    // Handle name change
    async handleNameChange(index) {
      if (!this.requiresPro('Customizing speaker names')) {
        // Revert the change
        this.internalSpeakers[index].name = this.speakers[index].name;
        this.$forceUpdate();
        return;
      }

      // Get the original name before the change
      const oldName = this.speakers[index].name;
      const newName = this.internalSpeakers[index].name;

      // Only proceed if there's actually a change
      if (oldName === newName) return;

      // Emit event with updated speaker data
      this.$emit('name-changed', {
        index,
        newName,
        oldName
      });

      // Save the customizations
      await this.saveSpeakerCustomizations();

      // Add a small delay to ensure the parent component has time to process the change
      setTimeout(() => {
        // If the active speaker was renamed, update the reference
        if (this.activeSpeaker === oldName) {
          this.$emit('speaker-selected', newName);
        }
      }, 100);
    },

    // Handle role change
    async handleRoleChange(index) {
      if (!this.requiresPro('Customizing speaker roles')) {
        // Revert the change
        this.internalSpeakers[index].role = this.speakers[index].role;
        this.$forceUpdate();
        return;
      }

      // Emit event with updated role data
      this.$emit('role-changed', {
        index,
        speakerName: this.internalSpeakers[index].name,
        newRole: this.internalSpeakers[index].role,
        oldRole: this.speakers[index].role
      });

      // Save the customizations
      await this.saveSpeakerCustomizations();
    },

    // Handle voice change
    async handleVoiceChange(index) {
      if (!this.requiresPro('Selecting custom voices')) {
        // Revert the change
        this.$set(this.selectedVoices, index, '');
        this.$forceUpdate();
        return;
      }

      // Emit event with voice selection data
      this.$emit('voice-changed', {
        index,
        speakerName: this.internalSpeakers[index].name,
        voiceId: this.selectedVoices[index] || ''
      });

      // Save the customizations
      await this.saveSpeakerCustomizations();
    }
  }
}
</script>

<style scoped>
.speaker-customization-panel {
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.theme--dark .speaker-customization-panel {
  background-color: #1a202c;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.panel-title-area {
  display: flex;
  align-items: center;
}

.panel-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.theme--dark .panel-title {
  color: #e2e8f0;
}

.customization-count {
  font-size: 0.75rem;
  color: #718096;
  margin-left: 0.25rem;
}

.toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.theme--dark .toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.toggle-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .toggle-btn:hover {
  background-color: #2d3748;
}

.panel-content {
  margin-top: 0.75rem;
}

.settings-section {
  margin-bottom: 2rem;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2d3748;
}

.theme--dark .settings-title {
  color: #e2e8f0;
}

/* Transcript customization styles */
.transcript-customization {
  margin-bottom: 1.5rem;
}

.customization-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
}

.theme--dark .customization-label {
  color: #a0aec0;
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.opacity-slider {
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  background: #e2e8f0;
  border-radius: 3px;
  outline: none;
}

.theme--dark .opacity-slider {
  background: #475569;
}

.opacity-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
}

.opacity-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
}

.opacity-value {
  width: 40px;
  font-size: 0.875rem;
  text-align: right;
  color: #4a5568;
}

.theme--dark .opacity-value {
  color: #a0aec0;
}

.color-picker-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.current-color {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #cbd5e0;
}

.theme--dark .current-color {
  border-color: #475569;
}

.color-picker {
  -webkit-appearance: none;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: transparent;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}

.reset-button {
  margin-left: auto;
  background: #f1f5f9;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.theme--dark .reset-button {
  background: #334155;
  color: #94a3b8;
}

.reset-button:hover {
  background: #e2e8f0;
  color: #475569;
}

/* Background upload styles */
.background-upload {
  width: 100%;
}

.upload-bg-label {
  display: block;
  cursor: pointer;
  width: 100%;
}

.bg-preview-container {
  width: 100%;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e0;
  background-color: #f8fafc;
  transition: all 0.3s ease;
}

.theme--dark .bg-preview-container {
  border-color: #475569;
  background-color: #1e293b;
}

.bg-preview-container:hover {
  border-color: #3b82f6;
  background-color: #f1f5f9;
}

.bg-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bg-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #64748b;
}

.theme--dark .bg-placeholder {
  color: #94a3b8;
}

/* Speaker customization grid */
.speakers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.speaker-customize-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.speaker-image-upload {
  width: 100%;
}

.preview-container {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e0;
  background-color: #f8fafc;
  margin: 0 auto;
  transition: all 0.3s ease;
}

.theme--dark .preview-container {
  border-color: #475569;
  background-color: #1e293b;
}

.preview-container:hover {
  border-color: #3b82f6;
  transform: scale(1.05);
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
  text-align: center;
  padding: 0.5rem;
}

.theme--dark .upload-placeholder {
  color: #94a3b8;
}

.file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
}

.speaker-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.name-input, .role-input, .voice-select {
  width: 100%;
  padding: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  transition: all 0.3s;
  background-color: #ffffff;
  color: #1a202c;
}

.theme--dark .name-input,
.theme--dark .role-input,
.theme--dark .voice-select {
  border-color: #475569;
  background-color: #1e293b;
  color: #e2e8f0;
}

.name-input:focus,
.role-input:focus,
.voice-select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

/* Voice selection styles */
.voice-select-container {
  position: relative;
}

.voice-select-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
}

.theme--dark .voice-select-label {
  color: #a0aec0;
}

.voice-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
  padding-right: 2rem;
}

.theme--dark .voice-select {
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%239CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
}

/* Mobile responsive design */
@media (max-width: 768px) {
  .speakers-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 1rem;
  }

  .panel-title {
    font-size: 0.8125rem;
  }

  .settings-title {
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .speakers-grid {
    grid-template-columns: 1fr;
  }
}

.reset-image-button, .reset-background-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin: 0.5rem auto 0;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  color: #ef4444;
  background-color: #fee2e2;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.theme--dark .reset-image-button,
.theme--dark .reset-background-button {
  background-color: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.reset-image-button:hover,
.reset-background-button:hover {
  background-color: #fecaca;
}

.theme--dark .reset-image-button:hover,
.theme--dark .reset-background-button:hover {
  background-color: rgba(239, 68, 68, 0.3);
}

.reset-background-button {
  width: fit-content;
  margin-top: 0.75rem;
}

.reset-background-button {
  width: fit-content;
  margin-top: 0.75rem;
}
</style>
